import React, { useState, useEffect } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
} from "reactstrap"
import { withTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import BootstrapTable from "react-bootstrap-table-next"
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit"
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
} from "react-bootstrap-table2-paginator"
import { formatDate, IsPartnerCockpitModuleEnabled } from "helpers/misc_helpers"
import PartFilter from "components/Common/PartFilters"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useLoading } from "helpers/custom_hooks"
import { GetWatchlist } from "helpers/backend_helper"

const DemandList = props => {
  const [demands, setDemands] = useState([])
  const setLoading = useLoading()
  const [filteredDemands, setFilteredDemands] = useState([])
  const [selectedPartnerCockpit, setSelectedPartnerCockpit] = useState(null)
  const [partnerCockpitEnabled, setPartnerCockpitEnabled] = useState(false)

  const loadPermissions = async _ => {
    const isEnabled = await IsPartnerCockpitModuleEnabled()
    setPartnerCockpitEnabled(isEnabled)
  }

  useEffect(() => {
    loadPermissions()
  }, [])

  // Convert partner cockpits to select options
  const cockpitOptions = [{ value: "all", label: "All Partner Cockpits" }]

  // Handle partner cockpit filter change
  useEffect(() => {
    if (!selectedPartnerCockpit || selectedPartnerCockpit.value === "all") {
      setFilteredDemands(demands)
    } else {
      const filtered = demands.filter(
        demand => demand.partnerCockpit === selectedPartnerCockpit.label
      )
      setFilteredDemands(filtered)
    }
  }, [selectedPartnerCockpit, demands])

  const loadData = async _ => {
    setLoading(true)
    const result = await GetWatchlist()
    setDemands(result)
    setLoading(false)
  }

  useEffect(() => {
    loadData()
  }, [])

  const columns = [
    {
      dataField: "internalPart",
      text: props.t("CPN"),
      sort: true,
    },
    {
      dataField: "partNumber",
      text: props.t("Part no."),
      sort: true,
    },
    {
      dataField: "description",
      text: props.t("Description"),
      sort: true,
    },
    {
      dataField: "manufacturer",
      text: props.t("Manufacturer"),
      sort: true,
    },
    {
      dataField: "quantity",
      text: props.t("Required Qty"),
      sort: true,
      formatter: cell => <>{cell} pc.</>,
      headerStyle: _ => ({
        textAlign: "right",
      }),
      align: () => "right",
    },
    {
      dataField: "price",
      text: props.t("Target Price"),
      sort: true,
      formatter: (cell, row) => (
        <>{cell ? cell.toFixed(5) + " " + row.currency : "-"}</>
      ),
      headerStyle: _ => ({
        textAlign: "right",
      }),
      align: () => "right",
    },
    {
      dataField: "partnerCockpitName",
      text: props.t("Partner Cockpit"),
      sort: true,
    },
    {
      dataField: "createdAt",
      text: props.t("Last Update"),
      sort: true,
      formatter: cell => formatDate(cell),
    },
    {
      dataField: "actions",
      text: props.t("Actions"),
      formatter: (cell, row) => (
        <Link
          to={`/partner-cockpit/demand-details/${row.id}`}
          className="btn btn-sm btn-primary"
        >
          {props.t("View Details")}
        </Link>
      ),
    },
  ]

  const pageOptions = {
    sizePerPage: 10,
    totalSize: filteredDemands.length,
    custom: true,
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {partnerCockpitEnabled ? (
            <>
              <Breadcrumbs
                title={props.t("Partner Cockpit")}
                breadcrumbItem={props.t("Demand List")}
                showBackButton
              />
              <Card>
                <CardBody>
                  <PaginationProvider
                    pagination={paginationFactory(pageOptions)}
                    keyField="id"
                    columns={columns}
                    data={filteredDemands}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <ToolkitProvider
                        keyField="id"
                        data={filteredDemands}
                        columns={columns}
                        bootstrap4
                        search
                      >
                        {toolkitProps => (
                          <React.Fragment>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h4 className="mb-0">{props.t("Demand List")}</h4>
                              <Link
                                to="/watchlist-upload"
                                className="btn btn-primary"
                              >
                                {props.t("Upload Demands")}
                              </Link>
                            </div>

                            <PartFilter
                              isOpen={true}
                              parts={demands}
                              setParts={setFilteredDemands}
                              searchProps={toolkitProps.searchProps}
                              showPartnerCockpitFilter={true}
                              partnerCockpitFilter={{
                                value: selectedPartnerCockpit,
                                onChange: setSelectedPartnerCockpit,
                                options: cockpitOptions,
                              }}
                              showCPNFilter={true}
                              isWatchlist
                            />

                            <div className="table-responsive mt-4">
                              <BootstrapTable
                                {...toolkitProps.baseProps}
                                {...paginationTableProps}
                                bordered={false}
                                striped
                                hover
                                classes={"table align-middle table-nowrap"}
                                headerWrapperClasses={"table-light"}
                              />
                              {filteredDemands.length === 0 && (
                                <p className="text-center">
                                  {props.t("No demands available")}
                                </p>
                              )}
                            </div>
                            <div className="pagination pagination-rounded justify-content-end mb-2">
                              <PaginationListStandalone {...paginationProps} />
                            </div>
                          </React.Fragment>
                        )}
                      </ToolkitProvider>
                    )}
                  </PaginationProvider>
                </CardBody>
              </Card>
            </>
          ) : (
            <Row>
              <Col lg="12">
                <div className="text-center mb-5">
                  <h1 className="display-2 fw-medium">
                    4<i className="bx bx-buoy bx-spin text-primary display-3" />
                    3
                  </h1>
                  <h4 className="text-uppercase">
                    Sorry, you are not allowed to view this page
                  </h4>
                  <p className="text-primary">
                    This is a premium feature. To use this feature, please
                    upgrade your license!
                  </p>
                  <div className="mt-5 text-center">
                    <Link
                      className="btn btn-primary waves-effect waves-light "
                      to="/settings?tab=2"
                    >
                      Upgrade Account
                    </Link>
                    <br />
                    <Link className=" d-block mt-4" to="/dashboard">
                      <i className="bx bx-left-arrow-alt" />
                      Back to Dashboard
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(DemandList)
